import { useState, useCallback, useId, useMemo } from "react";
import { useApi } from "@/hooks/use-api";
import { apiFetch } from "@/lib/api-client";
import { AbExperimentDetail } from "@/components/pages/ab-experiment-detail";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

type AbStatus = "draft" | "running" | "paused" | "concluded";

interface AbExperiment {
  id: string;
  pageId: string;
  name: string;
  status: AbStatus;
  trafficSplit: Record<string, number>;
  winnerId: string | null;
  concludedAt: string | null;
  createdBy: string;
  createdAt: string;
}

interface PageSummary {
  id: string;
  title: string;
  slug: string;
  status: "draft" | "published" | "archived";
  currentVersionId: string | null;
  updatedAt: string;
}

interface PageListResponse {
  items: PageSummary[];
  total: number;
}

const STATUS_STYLES: Record<AbStatus, string> = {
  draft: "bg-[var(--color-muted)] text-[var(--color-foreground)]",
  running: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  paused: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  concluded: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
};

function StatusBadge({ status }: { status: AbStatus }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}>
      {status}
    </span>
  );
}

function CreateForm({
  pages,
  onCreated,
  onCancel,
}: {
  pages: PageSummary[];
  onCreated: (experiment: AbExperiment, pageId: string) => void;
  onCancel: () => void;
}) {
  const nameId = useId();
  const pageFieldId = useId();
  const [name, setName] = useState("");
  const [selectedPageId, setSelectedPageId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!name.trim() || !selectedPageId) {
      setFormError("Name and page are required.");
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const response = await apiFetch<{ data: AbExperiment }>(`/crm/pages/${selectedPageId}/experiments`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim() }),
      });
      onCreated(response.data, selectedPageId);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create experiment");
    } finally {
      setIsSubmitting(false);
    }
  }, [name, onCreated, selectedPageId]);

  return (
    <div className="rounded-md border border-[var(--color-border)] p-6 space-y-4">
      <div>
        <h3 className="font-semibold text-[var(--color-foreground)]">Create experiment</h3>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          This creates a real draft experiment for the selected page. Variant setup uses saved page versions in the detail view.
        </p>
      </div>

      {formError && (
        <p className="text-sm text-[var(--color-destructive)]">{formError}</p>
      )}

      <div>
        <label htmlFor={nameId} className="block text-sm font-medium text-[var(--color-foreground)]">
          Experiment name
        </label>
        <input
          id={nameId}
          type="text"
          placeholder="e.g. Homepage CTA test"
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="mt-1 w-full rounded-md border border-[var(--color-input)] bg-[var(--color-background)] px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label htmlFor={pageFieldId} className="block text-sm font-medium text-[var(--color-foreground)]">
          Select page
        </label>
        <select
          id={pageFieldId}
          value={selectedPageId}
          onChange={(event) => setSelectedPageId(event.target.value)}
          className="mt-1 w-full rounded-md border border-[var(--color-input)] bg-[var(--color-background)] px-3 py-2 text-sm"
        >
          <option value="">Choose a page…</option>
          {pages.map((page) => (
            <option key={page.id} value={page.id}>
              {page.title || page.slug}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary)]/90 disabled:opacity-50"
        >
          {isSubmitting ? "Creating…" : "Create experiment"}
        </button>
        <button
          onClick={onCancel}
          className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-accent)]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function ExperimentCard({
  experiment,
  onSelect,
}: {
  experiment: AbExperiment;
  onSelect: () => void;
}) {
  const splitValues = Object.values(experiment.trafficSplit);
  const splitLabel = splitValues.length > 0
    ? `${splitValues.join(" / ")}%`
    : "Not configured";

  return (
    <button
      onClick={onSelect}
      className="stagger-item w-full rounded-md border border-[var(--color-border)] p-4 text-left transition-colors hover:bg-[var(--color-accent)]"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-medium text-[var(--color-foreground)]">{experiment.name}</h3>
          <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
            Created {new Date(experiment.createdAt).toLocaleDateString()} · Split: {splitLabel}
          </p>
        </div>
        <StatusBadge status={experiment.status} />
      </div>
    </button>
  );
}

export default function AbTestingPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState("");
  const [selectedExpId, setSelectedExpId] = useState<string | null>(null);
  const pageSelectId = useId();

  const {
    data: pagesData,
    isLoading: pagesLoading,
    error: pagesError,
  } = useApi<PageListResponse>("/crm/pages?limit=100");
  const pages = pagesData?.items ?? [];
  const selectedPage = useMemo(
    () => pages.find((page) => page.id === selectedPageId) ?? null,
    [pages, selectedPageId],
  );
  const {
    data: experiments,
    isLoading: experimentsLoading,
    error: experimentsError,
    refetch: refetchExperiments,
  } = useApi<AbExperiment[]>(
    selectedPageId ? `/crm/pages/${selectedPageId}/experiments` : "",
    { enabled: !!selectedPageId },
  );

  if (selectedExpId && selectedPageId) {
    return (
      <AbExperimentDetail
        pageId={selectedPageId}
        experimentId={selectedExpId}
        onBack={() => {
          setSelectedExpId(null);
          refetchExperiments();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">A/B Testing</h1>
          <p className="mt-1 text-[var(--color-muted-foreground)]">
            Manage real page experiments backed by the existing builder runtime.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          disabled={pages.length === 0}
          className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] hover:bg-[var(--color-primary)]/90 disabled:opacity-50"
        >
          New experiment
        </button>
      </div>

      {showCreate && (
        <CreateForm
          pages={pages}
          onCreated={(experiment, pageId) => {
            setSelectedPageId(pageId);
            setSelectedExpId(experiment.id);
            setShowCreate(false);
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <div className="space-y-3 rounded-md border border-[var(--color-border)] p-4">
        <div>
          <label htmlFor={pageSelectId} className="block text-sm font-medium text-[var(--color-foreground)]">
            Select a page
          </label>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            Experiments are page-scoped. Choose a page to inspect active and historical tests.
          </p>
        </div>

        {pagesLoading ? (
          <Skeleton className="h-10 w-full max-w-md" />
        ) : (
          <select
            id={pageSelectId}
            value={selectedPageId}
            onChange={(event) => {
              setSelectedPageId(event.target.value);
              setSelectedExpId(null);
            }}
            className="w-full max-w-md rounded-md border border-[var(--color-input)] bg-[var(--color-background)] px-3 py-2 text-sm"
          >
            <option value="">Choose a page…</option>
            {pages.map((page) => (
              <option key={page.id} value={page.id}>
                {page.title || page.slug}
              </option>
            ))}
          </select>
        )}

        {pagesError && (
          <p className="text-sm text-[var(--color-destructive)]">{pagesError}</p>
        )}
      </div>

      {selectedPage && (
        <div className="rounded-md border border-[var(--color-border)] p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-semibold text-[var(--color-foreground)]">{selectedPage.title || selectedPage.slug}</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                /{selectedPage.slug} · Last updated {new Date(selectedPage.updatedAt).toLocaleDateString()}
              </p>
            </div>
            <div className="text-sm text-[var(--color-muted-foreground)]">
              {selectedPage.currentVersionId
                ? "Saved versions can be attached as experiment variants."
                : "This page has no current saved version yet; create content in the editor first."}
            </div>
          </div>
        </div>
      )}

      {selectedPageId && (
        <div className="space-y-3">
          {experimentsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
              <SkeletonCard className="h-32" />
            </div>
          ) : experimentsError ? (
            <div className="rounded-md border border-[var(--color-border)] p-6">
              <p className="text-sm text-[var(--color-destructive)]">{experimentsError}</p>
            </div>
          ) : !experiments || experiments.length === 0 ? (
            <div className="rounded-md border border-[var(--color-border)] p-12 text-center">
              <p className="text-4xl">A/B</p>
              <p className="mt-4 font-medium text-[var(--color-foreground)]">No experiments on this page yet</p>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Create one to attach saved page versions, configure traffic split, and then start serving it through the existing runtime.
              </p>
            </div>
          ) : (
            experiments.map((experiment) => (
              <ExperimentCard
                key={experiment.id}
                experiment={experiment}
                onSelect={() => setSelectedExpId(experiment.id)}
              />
            ))
          )}
        </div>
      )}

      {!selectedPageId && !showCreate && (
        <div className="rounded-md border border-[var(--color-border)] p-12 text-center">
          <p className="text-4xl">A/B</p>
          <p className="mt-4 font-medium text-[var(--color-foreground)]">
            {pagesLoading ? "Loading pages…" : "Select a page to get started"}
          </p>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            This surface lists real experiments only. It does not invent placeholder rows or mock results.
          </p>
        </div>
      )}
    </div>
  );
}
